import React from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { Field } from 'redux-form';
import { changeTab } from 'containers/ChannelsPage/actions';
import {
  selectChannelFormGifMode,
  selectChannelFormWithFilter,
  selectChannelModeId,
  selectChannelImageCustomization,
} from 'containers/ChannelsPage/selectors';

import { LargeLabel, DescriptionLabel, FileWrapper } from 'components/InputField/styledComponents';
import Button from 'components/Button';
import Divider from 'components/Divider';
import {
  PhotoFilterPreview
} from './components';
import {
  GifStyleWrapper,
  PhotoOptionsWrapper,
  GifStyleFieldLabel,
  StyledForm,
  RadioInputWrapper,
  UpgradeText,
  FieldHead,
} from './styledComponents';
import { ToastContainer, toast } from 'react-toastify';
import Icon from 'components/Icon';
import Tooltip from '../../../components/Tooltip';

const notify = () => toast("Changes Saved!");

const captureModes = [
  {
    shortname: '4pose',
    label: 'Classic GIF',
    description: 'Classic GIF mode captures four distinct photos, giving your guests a few seconds to change up their pose between each one, then animates them into a loop where each photo plays in order.',
    value: (data) => {
      var value = Object.values(data).filter((val) => val.shortname === '4pose')[0];
      return value ? value.id : 1;
    }
  },
  {
    shortname: 'tnb',
    label: 'Boomerang GIF',
    description: 'Boomerang GIF mode captures about 1 second of real-time video of your guests, then animates it into a loop that plays their video forward and then in reverse',
    value: (data) => {
      var value = Object.values(data).filter((val) => val.shortname === 'tnb')[0];
      return value ? value.id : 2;
    }
  },
  {
    shortname: 'still',
    label: 'Still',
    description: 'Still mode takes the move out of Movebooth and captures a single photo of your guests.',
    value: (data) => {
      var value = Object.values(data).filter((val) => val.shortname === 'still')[0];
      return value ? value.id : 3;
    }
  },
  {
    shortname: 'video',
    label: 'Story Video',
    description: 'Story Video mode captures a 15 second real-time video of your guests, with audio. This mode is best paired with a printed poster next to your selfie station that provides a prompt for your guests to talk about. For example, “Describe the best meal you ever had in 15 seconds!” or “Give your best piece of marriage advice to the couple in 15 seconds!',
    value: (data) => {
      var value = Object.values(data).filter((val) => val.shortname === 'story')[0];
      return value ? value.id : 4;
    }
  },
];

const imageStyles = [
  {
    shortname: 'tnb',
    label: 'Standard',
    description: 'The Standard image style will not apply any effects to your photos.',
    value: (_data) => 'standard'
  },
  {
    shortname: 'bnw',
    label: 'Black & White',
    description: 'The Black & White image style will apply an attractive and contrasty black-and-white color palette to your photos',
    value: (_data) => 'bnw'
  },
  {
    shortname: 'glitch',
    label: 'Glitch',
    description: 'The Glitch image style will make your photos black & white and apply an anaglyph 3d-like effect to them–reminiscent of old 3d glasses that used red and blue lenses–to provide a glitchy look to your photos',
    value: (_data) => 'glitch'
  },
]

const GifStyleField = ({
  label,
  selectedTheme,
  themeInstance,
  value,
  gifMode,
  shortname,
  name,
  description,
  selectedValue,
}) => {
  const handleChange = (e, input) => {
    const value = e.target.value;
    input.value
      ? input.onChange(name === 'mode_id'
        ? value
        : {
          id: value.id,
          is_black_and_white_mode_enabled: value === 'bnw',
          is_glitch_mode_enabled: value === 'glitch',
        })
      : input.onChange(null)
  }
  return (
    <GifStyleFieldLabel className={parseInt(value, 10) === parseInt(gifMode, 10) ? 'selected' : ''}>
      <PhotoFilterPreview
        med
        selectedTheme={selectedTheme}
        themeInstance={themeInstance}
        noMargin={true}
        center={true}
        photoOptions={shortname}
      />
      <RadioInputWrapper>
        <div className='radio-input-container'>
          <Field
            name={name}
            component={({ input, type }) => (
              <input
                {...input}
                checked={value == selectedValue}
                onChange={e => handleChange(e, input)}
                type={type}
              />
            )
            }
            value={value}
            type='radio'
          />
          <label>{label}</label>
        </div>
        <Tooltip offset='-10px' width='200px' position='bottom' text={description}>
          <Icon size='12' name='help' />
        </Tooltip>
      </RadioInputWrapper>

    </GifStyleFieldLabel>
  )
};

const PhotoOptionsSection = ({ groupName, header, description, type, modes, ...props }) => (
  <FileWrapper>
    {/* <LargeLabel>{header}</LargeLabel>
    <DescriptionLabel>{description}</DescriptionLabel> */}
    <FieldHead className="brand">
          <strong>{header}</strong>
          <h3>{description}</h3>
        </FieldHead>
    <GifStyleWrapper>
      {(type === 'capture' ? captureModes : imageStyles).map((item, idx) => (
        <GifStyleField
          key={idx}
          name={groupName}
          {...props}
          {...item}
          value={item.value(modes)}
        />
      ))}
    </GifStyleWrapper>
  </FileWrapper>
)

const PhotoOptionsForm = ({
  handleSubmit,
  submitting,
  themeId,
  isNew,
  channelThemes,
  themeInstanceId,
  channelThemeInstances,
  goToThemeTab,
  disabled,
  edit,
  modes,
  nextTab,
  modeId,
  imageCustomization,
}) => {
  const selectedTheme = channelThemes[themeId];
  const themeInstance = isNew ? null : channelThemeInstances[themeInstanceId];
  return (
    <StyledForm onSubmit={handleSubmit} className="channel_forms--photo-options">
      <ToastContainer />
      <PhotoOptionsWrapper>
        <PhotoOptionsSection
          groupName='mode_id'
          header='Capture Mode'
          description='Select the capture mode you’d like to use for this channel.'
          type='capture'
          selectedTheme={selectedTheme}
          themeInstance={themeInstance}
          gifMode={modeId}
          modes={modes}
          selectedValue={modeId}
        />
        <Divider marginBottom={20}></Divider>
        <PhotoOptionsSection
          groupName='channel_image_customization'
          header='Image Style'
          description='Select a style to apply to the images captured.'
          type='image'
          selectedTheme={selectedTheme}
          themeInstance={themeInstance}
          gifMode={modeId}
          modes={modes}
          selectedValue={
            imageCustomization
              ? (imageCustomization.is_black_and_white_mode_enabled ? 'bnw' : imageCustomization.is_glitch_mode_enabled ? 'glitch' : 'standard')
              : ''}
        />
      </PhotoOptionsWrapper>
      <Divider marginBottom={48}></Divider>
      <UpgradeText className='left-pos pdd-20'>Looking for more? Send us a suggestion.</UpgradeText>
      <div className='channel-forms__group'>
        {edit
          ? <Button type='submit' onClick={notify} disabled={submitting || !modeId}>Save</Button>
          : <Button type='button' onClick={() => nextTab()} disabled={submitting || !modeId}>Save & Continue</Button>
        }
      </div>
    </StyledForm>
  );
}

const mapStateToProps = createStructuredSelector({
  gifMode: selectChannelFormGifMode(),
  withFilter: selectChannelFormWithFilter(),
  modeId: selectChannelModeId(),
  imageCustomization: selectChannelImageCustomization(),
});

const mapDispatchToProps = (dispatch) => ({
  goToThemeTab: () => dispatch(changeTab(1)),
});

export default connect(mapStateToProps, mapDispatchToProps)(PhotoOptionsForm);



// WEBPACK FOOTER //
// ./src/containers/ChannelsPage/Forms/PhotoOptionsForm.js